"use client";

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { DiscoverCategoryPanel } from "@/components/feed/discover-category-panel";
import type { FeedFilterState } from "@/hooks/use-paginated-feed";
import { Z } from "@/lib/overlay/z-scale";

export interface DiscoverCategoryFilterOption {
  id: string;
  name: string;
  slug?: string | null;
}

export interface DiscoverCategorySelection {
  categorySlug: string;
  name: string;
  liveFilters: FeedFilterState;
  relativeFilterMode?: "brand" | "tcg";
  relativeFilterOptions?: DiscoverCategoryFilterOption[];
}

interface FeedDiscoverCategorySliderProps {
  open: boolean;
  onOpenChangeAction: (open: boolean) => void;
  selection: DiscoverCategorySelection | null;
  currentUserId?: string;
}

export function FeedDiscoverCategorySlider({
  open,
  onOpenChangeAction,
  selection,
  currentUserId,
}: FeedDiscoverCategorySliderProps) {
  const [mounted, setMounted] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!open) return;
    const body = document.body;
    const html = document.documentElement;
    const prevBodyOverflow = body.style.overflow;
    const prevHtmlOverflow = html.style.overflow;
    body.style.overflow = "hidden";
    html.style.overflow = "hidden";
    return () => {
      body.style.overflow = prevBodyOverflow;
      html.style.overflow = prevHtmlOverflow;
    };
  }, [open]);

  const handleClose = () => {
    onOpenChangeAction(false);
  };

  const content = (
    <AnimatePresence>
      {open && selection && (
        <motion.div
          initial={{ x: "100%" }}
          animate={{ x: 0 }}
          exit={{ x: "100%" }}
          transition={{ type: "tween", duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
          className="fixed top-0 right-0 flex h-dvh w-full flex-col bg-background"
          style={{ zIndex: Z.overlayPanel }}
        >
          <div
            ref={scrollRef}
            className="flex min-h-0 flex-1 flex-col overflow-y-auto overflow-x-hidden overscroll-contain pb-[calc(3.5rem+var(--kraahl-safe-bottom))]"
          >
            <div className="mx-auto w-full max-w-(--breakpoint-lg) md:max-w-7xl xl:max-w-[min(100%,90rem)]">
              <DiscoverCategoryPanel
                categorySlug={selection.categorySlug}
                name={selection.name}
                mockListings={[]}
                liveFilters={selection.liveFilters}
                relativeFilterMode={selection.relativeFilterMode}
                relativeFilterOptions={selection.relativeFilterOptions}
                currentUserId={currentUserId}
                onBack={handleClose}
                scrollRootRef={scrollRef}
                compactGutter
              />
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  if (!mounted) return null;
  return createPortal(content, document.body);
}
